import { useEffect, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { getAuthTransition, setAuthTransition } from "../lib/auth-transition";
import type { Stage } from "./routing";
import { carriedNext, destinationFor, sanitiseNext } from "./routing";
import { useSession } from "./SessionProvider";
import { SplashScreen } from "../routes/SplashScreen";

/**
 * Route guards, expressed as "which stage of the routing matrix may see this".
 *
 * A guard that fails does not render a redirect of its own invention: it asks
 * `destinationFor` — the same function the splash uses — so there is exactly
 * one description of where a session belongs. Adding a screen means naming its
 * stage, not writing a fourth branch.
 *
 * While the session is still resolving, every guarded route renders the splash
 * (design spec §5: "and any protected route before auth resolves"), which
 * carries its own anti-flash rule.
 *
 * ── Guards are the ones that carry `?next=` (QA T-172 B1) ───────────────────
 *
 * A guard is not only the thing that *captures* a deep link on the way in; it
 * is also, and more often, the thing that fires on the way **out**. The
 * moment `reload()` advances the stage, the guard on the screen the player is
 * still looking at re-renders and redirects — before that screen's own
 * `navigate()` has been committed by the router. Whatever the guard decides is
 * therefore the answer, so a guard that forgets `?next=` is the whole defect,
 * no matter how correct the screen underneath it is.
 *
 * ── …which is also why the two §14.1 rulings live here ──────────────────────
 *
 * For the same reason, a guard is the thing that has to *not* fire. Both
 * amendments are one line each and both are LOCKED:
 *
 *  - `justConverted` → **freeze**. Render the children exactly as they were.
 *    The page under the conversion sheet must never visibly navigate (r2).
 *  - `loggingOut` → **redirect bare**. A log-out is a departure the player
 *    chose, so there is no interruption to record and `?next=` is dropped
 *    (ruling C). Doing it in the guard rather than only at the call sites is
 *    what makes it true even when the guard wins the race — which it usually
 *    does.
 */

export function RequireStage({ stage: allowed, children }: { stage: Stage; children?: ReactNode }) {
  const { status, stage, justConverted, loggingOut } = useSession();
  const location = useLocation();

  // §14.1 r2, LOCKED: frozen at the last render, before anything else.
  if (justConverted) return <>{children ?? <Outlet />}</>;

  if (status === "loading") return <SplashScreen routeOnResolve={false} />;

  if (stage !== allowed) {
    /**
     * Two different bounces, two different sources for `next`:
     *
     *  - **Out of a protected route** (`allowed === "ready"`, session not
     *    ready): the path the player was actually trying to reach *is* the
     *    deep link. Capture it.
     *  - **Off a mid-chain gate** (`/welcome`, once identity is confirmed):
     *    the deep link is already in the URL and this hop only has to not
     *    lose it.
     *
     * …unless the player asked to leave (§14.1 ruling C), in which case there
     * is no deep link — there is a person who pressed Log out.
     */
    const next = loggingOut
      ? null
      : allowed === "ready"
        ? sanitiseNext(location.pathname + location.search)
        : carriedNext(location.search);
    return <Navigate to={destinationFor(stage, next)} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * The auth screens: reachable only when there is no session.
 *
 * A signed-in player who navigates to `/login` is not shown a login form —
 * they are sent wherever their session belongs. Without this, "log in" is a
 * button that appears to work and then dumps you back where you started.
 */
export function RequireAnonymous({ children }: { children?: ReactNode }) {
  const { status, stage, justConverted, loggingOut } = useSession();
  const location = useLocation();

  /**
   * **Where both transitions end.**
   *
   * This is the only screen either of them is aiming at, and reaching it is the
   * one unambiguous signal that the hand-over is complete: the session has
   * resolved to nothing *and* the router has arrived somewhere a signed-out
   * player belongs. Lowering the flags anywhere earlier — in the log-out
   * handler, in `finishConversion`, in an ancestor's effect — lowers them while
   * the location is still the screen being left, where a guard is about to fire
   * one last time and would rewrite the destination.
   */
  const arrived = stage === "anonymous" && status !== "loading";
  useEffect(() => {
    if (arrived && getAuthTransition() !== "none") setAuthTransition("none");
  }, [arrived]);

  /**
   * The conversion handoff lands here while the app's view of the session is
   * still the revoked guest, so without the freeze this guard would read
   * `stage === "ready"` and bounce the player straight back off the log-in
   * screen they were just handed to (§14.1 r2).
   */
  if (justConverted) return <>{children ?? <Outlet />}</>;

  if (status === "loading") return <SplashScreen routeOnResolve={false} />;
  if (stage !== "anonymous") {
    // This is the redirect that actually decides where a login lands (see the
    // note on `RequireStage`), so it is also the one that must not drop the
    // `?next=` the login screen was handed.
    return (
      <Navigate
        to={destinationFor(stage, loggingOut ? null : carriedNext(location.search))}
        replace
      />
    );
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * `/verify` has two legitimate arrivals and only one of them has a session.
 *
 * Better Auth is configured `autoSignIn: false` + `requireEmailVerification`
 * (architecture §7), so a **fresh sign-up creates no session at all** — the
 * player is anonymous by the routing matrix's reckoning while being exactly
 * the person the verify screen exists for. Guarding this route on
 * `stage === "unverified"` bounced them straight back to `/login`, which is
 * how the flow looked in the first staging walk.
 *
 * So the gate is: an unverified *session*, or an `?email=` handed over by the
 * sign-up that just happened. Anything else goes through the matrix. The
 * screen itself grants nothing — resending is rate-limited per address and
 * verification still requires the emailed token.
 */
export function RequireVerifiable({ children }: { children?: ReactNode }) {
  const { status, stage, justConverted, loggingOut } = useSession();
  const location = useLocation();
  const [params] = useSearchParams();

  if (justConverted) return <>{children ?? <Outlet />}</>;
  if (status === "loading") return <SplashScreen routeOnResolve={false} />;
  if (stage === "unverified") return <>{children ?? <Outlet />}</>;
  if (stage === "anonymous" && params.get("email")) return <>{children ?? <Outlet />}</>;
  return (
    <Navigate
      to={destinationFor(stage, loggingOut ? null : carriedNext(location.search))}
      replace
    />
  );
}
